import { Profile } from "@/hooks/profile/types";
import { Button } from "@/components/ui/button";
import { Eye, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { WorkoutPlanForm } from "@/components/routines/workout-plans/WorkoutPlanForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TeacherPlansSectionProps {
  plans: any[];
  profile: Profile | null;
  openSection: string;
  setOpenSection: (section: string) => void;
  showForm: boolean;
  setShowForm: (show: boolean) => void;
  refetchPlans: () => void;
  routines: any[];
}

export const TeacherPlansSection = ({
  plans,
  profile,
  openSection,
  setOpenSection,
  showForm,
  setShowForm,
  refetchPlans,
  routines
}: TeacherPlansSectionProps) => {
  const navigate = useNavigate();

  // Filter professor's created plans (non-personal)
  const professorPlans = plans?.filter(plan => 
    !plan.is_personal && 
    plan.created_by === profile?.id &&
    plan.copied_from_plan_id === null  // Solo planes originales, no copias
  ) || [];

  const handleDeletePlan = async (planId: string) => {
    try {
      // Primero eliminamos las relaciones con las rutinas
      const { error: routinesError } = await supabase
        .from('workout_plan_routines')
        .delete()
        .eq('workout_plan_id', planId);

      if (routinesError) throw routinesError;

      // Luego eliminamos las suscripciones
      const { error: subscriptionsError } = await supabase
        .from('workout_plan_subscriptions')
        .delete()
        .eq('workout_plan_id', planId);

      if (subscriptionsError) throw subscriptionsError;

      // Finalmente eliminamos el plan
      const { error: planError } = await supabase
        .from('workout_plans')
        .delete()
        .eq('id', planId);

      if (planError) throw planError;

      toast({
        title: "Plan eliminado",
        description: "El plan ha sido eliminado correctamente",
      });

      refetchPlans();
    } catch (error: any) {
      console.error('Error deleting plan:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el plan",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-primary">Mis Planes Publicados</h2>
        <Dialog>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              className="bg-muted hover:bg-muted/80 text-foreground border-border"
            >
              <Plus className="mr-2 h-4 w-4" />
              Crear Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card/90 backdrop-blur-xl border-border">
            <WorkoutPlanForm onPlanAdded={refetchPlans} />
          </DialogContent>
        </Dialog>
      </div>
      
      {professorPlans.length > 0 ? (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {professorPlans.map((plan) => (
          <div
            key={plan.id}
            className="bg-card backdrop-blur-lg rounded-xl p-4 border border-primary/30"
          >
            <div className="flex flex-col h-full">
              <div className="flex-grow">
                <h3 className="text-lg font-semibold text-primary mb-2">{plan.name}</h3>
                {plan.description && (
                  <p className="text-sm bg-muted rounded-sm p-2 text-muted-foreground mb-4">{plan.description}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/rutina/plan/${plan.id}`)}
                  className="w-full bg-muted hover:bg-muted/80 text-foreground border-border"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Ver Rutinas
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full bg-destructive hover:bg-destructive/80 text-destructive-foreground border-destructive/20"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar Plan
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card/90 backdrop-blur-xl text-foreground border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                      <AlertDialogDescription className="text-muted-foreground">
                        Esta acción no se puede deshacer. Se eliminará el plan y todas sus rutinas asociadas.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-muted border-border hover:bg-muted/80">
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeletePlan(plan.id)}
                        className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                      >
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>Aún no has creado planes públicos.</p>
          <p className="text-sm mt-2">Haz click en "Crear Plan" para empezar.</p>
        </div>
      )}
    </div>
  );
};